import { useState, useRef } from 'react';
import { Upload, X, Loader2, FolderOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import MediaLibrary from './MediaLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ value, onChange, label, getToken }) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('upload');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      alert('Please select an image file (JPG, PNG, GIF, WebP, SVG)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large (max 10MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken ? getToken() : null;
      const res = await axios.post(`${API_URL}/api/admin/media/upload-gcs`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const url = res.data.url.startsWith('/') ? `${API_URL}${res.data.url}` : res.data.url;
      onChange(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid="image-upload">
      {value && (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Preview"
            className="h-24 rounded border border-slate-200 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
          >
            <X size={12} />
          </button>
        </div>
      )}

      <div className="flex gap-1 text-xs">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`px-2 py-1 rounded ${mode === 'upload' ? 'bg-slate-200 font-medium' : 'text-slate-500 hover:bg-slate-100'}`}
        >
          Upload
        </button>
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          className="px-2 py-1 rounded text-slate-500 hover:bg-slate-100 flex items-center gap-1"
        >
          <FolderOpen size={11} />
          Library
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`px-2 py-1 rounded ${mode === 'url' ? 'bg-slate-200 font-medium' : 'text-slate-500 hover:bg-slate-100'}`}
        >
          URL
        </button>
      </div>

      {mode === 'upload' ? (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="w-full"
          >
            {uploading ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload size={14} className="mr-2" /> Choose Image</>
            )}
          </Button>
        </div>
      ) : (
        <Input
          type="url"
          placeholder="https://example.com/image.jpg"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      <MediaLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={onChange}
        getToken={getToken}
      />
    </div>
  );
};

export default ImageUpload;
